import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import LabeledValue from '../../../../components/common/LabeledValue'
import Modal from '../../../../components/common/Modal/Modal'
import ValidationBanner from '../../../../components/common/ValidationBanner'
import Loader from '../../../../components/common/Loader'
import { TimeOffRequestDetails, isVacationBid } from '../../../../store/slices/Requestor/timeOffDetailsSlice'
import RightPanel from './RightPanel'
import CurrentRequestSection from './CurrentRequestSection'
import HistorySection from './HistorySection'
import WithdrawnView from './WithdrawnView'
import api from '../../../../api/axios'
import endpoints from '../../../../api/endpoints'
import useWithdrawRequest from '../../../../hooks/useWithdrawRequest'
import { isRequestDateBeforeToday } from '../../../../utils/date'
import { TABLE_COLUMNS, getDisplayedOtherHoursTotal } from './constants'

interface ReturnedViewProps {
  data: TimeOffRequestDetails
  tableRows: any[]
  highlightedDates: string[]
  handlePrint?: () => void

}

const RESUBMIT_MAX = 1000

const ReturnedView: React.FC<ReturnedViewProps> = ({ data, tableRows, highlightedDates, handlePrint }) => {
  const navigate = useNavigate()
  const empNuid = useSelector((state: any) => state.profile?.profile?.empNuid) || sessionStorage.getItem('userId')

  // Check if this is a vacation bid request
  const isVacationBidRequest = isVacationBid(data)

  /**
   * Vacation Bid's "Day in Other Requests" column renders as a checkbox
   * (matching legacy), not the numeric/breakdown value used for regular
   * time-off requests — same treatment as DraftView's Current Request table.
   */
  const currentRequestTableColumns = isVacationBidRequest
    ? TABLE_COLUMNS.map((col) =>
        col.key === 'hoursInOtherRequests'
          ? { ...col, label: 'Day in Other Requests' }
          : col
      )
    : TABLE_COLUMNS

  // tableRows comes from buildTableRows (constants.ts), which always returns
  // hoursInOtherRequests as a non-empty formatted string (e.g. "0.00") — never
  // use its JS truthiness directly for the checkbox; parse the actual total.
  const vacationBidTableRows = isVacationBidRequest
    ? tableRows.map((row) => ({
        ...row,
        hoursInOtherRequests: getDisplayedOtherHoursTotal(String(row.hoursInOtherRequests ?? '')) > 0,
      }))
    : tableRows

  const renderVacationBidOverlapCell = isVacationBidRequest
    ? (row: any, col: { key: string }) => {
        if (col.key !== 'hoursInOtherRequests') return row[col.key]
        if (row._totalRow || row.id === 'total') return ''
        return (
          <input
            type="checkbox"
            className="table-cell-checkbox"
            checked={!!row.hoursInOtherRequests}
            disabled
            readOnly
            aria-label="Day in other requests"
          />
        )
      }
    : undefined

  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false)
  const [withdrawn, setWithdrawn] = useState(false)
  const { withdraw, loading: withdrawLoading, error: withdrawError } = useWithdrawRequest()
  const [isEditingComment, setIsEditingComment] = useState(false)
  const [isEditingForEdit, setIsEditingForEdit] = useState(false)
  const [isResubmitted, setIsResubmitted] = useState(false)
  const [resubmitComment, setResubmitComment] = useState('')
  const [resubmittedOn, setResubmittedOn] = useState('')
  const [resubmittedComment, setResubmittedComment] = useState('')
  const [isSubmittingResubmit, setIsSubmittingResubmit] = useState(false)
  const [commentError, setCommentError] = useState<string | null>(null)
  
  // Disable Withdraw/Resubmit if the request contains any date before today
  // (e.g. a multi-day request that has already begun). isRequestDateBeforeToday
  // compares plain date strings (no Date-object timezone conversion), so the
  // button disables at end of day in the viewer's own timezone rather than
  // shifting by their UTC offset.
    const isActionDisabled = isRequestDateBeforeToday(data.todate)
  const location = useLocation()
  const isAdminMode = location?.pathname?.startsWith?.('/admin') || false
  if (withdrawn) {
    return (
      <WithdrawnView
        data={{ ...data, status: 'Withdrawn' }}
        tableRows={vacationBidTableRows}
        highlightedDates={highlightedDates}
        handlePrint={handlePrint}
        showSuccessBanner
      />
    )
  }

  const handleConfirmResubmit = async () => {
    // Validate that comments are not empty
    if (!resubmitComment.trim()) {
      setCommentError('Comments are required for resubmission')
      return
    }

    setCommentError(null)
    setIsSubmittingResubmit(true)

    try {
      // Call the resubmit API
      const endpoint = endpoints.requester.resubmitRequestNoChange(data.requestId)
      
      // Use different appCode based on request type
      const appCode = isVacationBidRequest ? 'EMP_VB' : 'EMP_CURRENT'
      
      await api.put(endpoint, {
        empNuid: empNuid,
        appCode: appCode,
        comments: resubmitComment.trim(),
      })

      // Success - update UI
      setResubmittedComment(resubmitComment)
      setResubmitComment('')
      const dt = new Date()
      setResubmittedOn(
        dt
          .toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short',
          })
          .replace(',', ''),
      )
      setIsResubmitted(true)
      setIsEditingComment(false)
    } catch (error: any) {
      console.error('Resubmit request failed:', error)
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to resubmit request. Please try again.'
      setCommentError(errorMessage)
    } finally {
      setIsSubmittingResubmit(false)
    }
  }

  /** Handles the withdraw request action from the modal. */
  const handleWithdrawConfirm = async (): Promise<boolean> => {
    const nuid = empNuid || sessionStorage.getItem('userId') || sessionStorage.getItem('nuid') || ''
    const requestId = data.requestId || ''
    if (!requestId || !nuid) {
      console.error('Missing required data for withdrawal:', { requestId, nuid })
      return false
    }
    const success = await withdraw(requestId, nuid)
    if (success) {
      setIsWithdrawOpen(false)
      setWithdrawn(true)
    }
    return success
  }

  /** Pre-compute whether to show request rank and its display value. */
  const showRequestRank = data.requestRank != null || isVacationBidRequest
  const requestRankDisplayValue = data.requestRank == null ? 'NA' : String(data.requestRank)

  /** Renders the action buttons/form based on current UI state. */
  const renderActionContent = (): React.ReactNode => {
    if (isResubmitted) {
      return (
        <div className="mt-4 p-4 bg-white">
          <div className="mb-3"><LabeledValue label="Resubmitted On" value={resubmittedOn} /></div>
          <div><LabeledValue label="Employee Comments" value={resubmittedComment || 'Employee comments'} /></div>
          <div className="mt-12!">
            <button className="button -primary" onClick={() => setIsWithdrawOpen(true)}>Withdraw Request</button>
          </div>
        </div>
      )
    }

    if (isEditingComment) {
      return (
        <div className="mt-8">
          <p className="p3 -medium mt-[8!">
            You are resubmitting this request without changes. Please enter comments for your approver.
          </p>
          <label htmlFor="resubmit-comments" className="block mb-1 font-medium">Comments</label>
          <textarea
            id="resubmit-comments"
            maxLength={RESUBMIT_MAX}
            value={resubmitComment}
            onChange={(e) => {
              setResubmitComment(e.target.value)
              setCommentError(null)
            }}
            className={`w-full p-2 border rounded resize-y ${commentError ? 'border-red-500' : ''}`}
            rows={4}
            disabled={isSubmittingResubmit}
          />
          {commentError && (
            <div className="flex items-center gap-2 text-sm text-red-500 mt-1">
              <img src="/assets/images/errorCircleSmall.svg" alt="error" style={{ width: 16, height: 16 }} />
              <span className='p4 -book'>{commentError}</span>
            </div>
          )}
          <div className="mt-8! flex gap-2 items-center">
            <button
              type="button"
              className="button"
              onClick={() => {
                setResubmitComment('')
                setIsEditingComment(false)
                setCommentError(null)
              }}
              disabled={isSubmittingResubmit}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button -primary"
              onClick={handleConfirmResubmit}
            >
              {isSubmittingResubmit ? 'Submitting...' : 'Yes, Resubmit'}
            </button>
          </div>
        </div>
      )
    }

    if (isEditingForEdit) {
      return (
        <div className="mt-8!">
          <p className="p3 -medium mb-2 p-2 text-sm">
            You are asking to make changes to this request prior to re-submission.
          </p>
          <div className="mt-12! flex gap-2 items-center">
            <button type="button" className="button" onClick={() => setIsEditingForEdit(false)}>Cancel</button>
            <button
              type="button"
              className="button -primary"
              onClick={() => {
                setIsEditingForEdit(false)
                navigate(`${isAdminMode ? '/admin' : ''}/requestor/edit-submitted/${data.requestId}`)
              }}
            >
              Yes, Allow Edit for Resubmit
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className="mt-12! flex flex-row gap-1 flex-wrap">
        <button
          className="button -primary"
          style={{ whiteSpace: 'nowrap' }}
          disabled={isActionDisabled}
          onClick={() => setIsWithdrawOpen(true)}
        >
          Withdraw Request
        </button>

        <button
          className="button -primary whitespace-nowrap min-w-max"
          style={{ whiteSpace: 'nowrap' }}
          disabled={isActionDisabled}
          onClick={() => {
            setIsEditingComment(true)
            setResubmitComment('')
          }}
        >
          Resubmit Request
        </button>

        <button
          className="button -primary whitespace-nowrap min-w-max"
          style={{ whiteSpace: 'nowrap' }}
          disabled={isActionDisabled}
          onClick={() => {
            setIsEditingForEdit(true)
            setIsEditingComment(false)
          }}
        >
          Edit and Resubmit Request
        </button>
      </div>
    )
  }

  return (
    <div>
      {isSubmittingResubmit && <Loader />}
       <h1 className="h1">{isVacationBidRequest?"Submitted-Returned Vacation Bid Request":"Submitted-Returned Time Off Request"}</h1>
      {commentError && (
        <ValidationBanner
          variant="error"
          heading={"Overlapping time off request conflict."}
          className="max-w-4xl"
        />
      )}
      <div className="flex flex-col mobile:gap-4 tablet:gap-6 desktop:gap-8 desktop:flex-row mt-6">
        <div className="flex-1">
          {data.returnedOn && <div className="mb-4"><LabeledValue label="Returned On" value={data.returnedOn} /></div>}
          {data.returnedBy && <div className="mb-4"><LabeledValue label="Returned By" value={data.returnedBy} /></div>}
          {data.returnReason && <div className="mb-4"><LabeledValue label="Return Reason" value={data.returnReason} /></div>}
          {data.approverComments && <div className="mb-4"><LabeledValue label="Approver Comments" value={data.approverComments} /></div>}
          {showRequestRank && <div className="mb-4"><LabeledValue label="Request Rank" value={requestRankDisplayValue} /></div>}
          {renderActionContent()}
        </div>
        <RightPanel data={data} handlePrint={handlePrint} />
      </div>
      <hr className="-variation-1 mt-16!" />
      {data.history && data.history.length > 0 && <HistorySection history={data.history} />}
      <CurrentRequestSection
        tableRows={vacationBidTableRows}
        highlightedDates={highlightedDates}
        tableColumns={currentRequestTableColumns}
        renderCell={renderVacationBidOverlapCell}
      />
      <Modal
        isOpen={isWithdrawOpen}
        title="Withdraw Request"
        onClose={() => setIsWithdrawOpen(false)}
        submitBtn="Withdraw Request"
        backBtn="Cancel"
        loading={withdrawLoading}
        onPrimary={handleWithdrawConfirm}
        onBack={() => setIsWithdrawOpen(false)}
      >
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <img className="w-10 h-10 shrink-0" src="/assets/images/alertSolid.svg" alt="warning" />
          <div>
            <p className="p2 -semibold">
              You are requesting to withdraw this time off request. It will be removed from the approvers queue.
            </p>
            {withdrawError && <p className="p3 -book text-red-600 mt-2">{withdrawError}</p>}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ReturnedView
